import { requiredHttpUrl } from "@/utils/zod/required-http-url";
import { z } from "zod";

/**
 * Schema for validating environment variables
 * Ensures all required configuration values are present and valid
 */
export const envSchema = z.object({
  // Build mode - must be development, production, or test
  MODE: z.enum(["development", "production", "test"]),

  // Application environment - determines which backend to connect to
  VITE_ENVIRONMENT: z.enum(["development", "staging", "production"]),

  // API domain URL - base domain for API requests
  VITE_API_DOMAIN: requiredHttpUrl("VITE_API_DOMAIN is required", "Invalid API domain URL"),

  // API URL - full URL for API endpoints
  VITE_API_URL: requiredHttpUrl("VITE_API_URL is required", "Invalid API URL"),

  // Remote app URL - URL for the remote application
  VITE_REMOTE_APP_URL: requiredHttpUrl("VITE_REMOTE_APP_URL is required", "Invalid remote app URL"),

  // Base URL for the AgentCore SSE endpoint, e.g. https://<cf-domain>/<env>/api/v1/agent.
  // The streaming client POSTs to `${VITE_AGENT_API_URL}/chat`. Different auth surface
  // from VITE_API_URL (JWT-only, no x-api-key/x-user-id), so kept as its own var.
  VITE_AGENT_API_URL: requiredHttpUrl("VITE_AGENT_API_URL is required", "Invalid agent API URL"),

  // API key for authentication
  VITE_API_KEY: z.string().min(1, "VITE_API_KEY is required"),

  // Clerk authentication environment
  VITE_CLERK_ENV: z.enum(["development", "production"]),

  // Clerk publishable key for client-side authentication
  VITE_CLERK_PUBLISHABLE_KEY: z.string().min(1, "VITE_CLERK_PUBLISHABLE_KEY is required"),

  // Optional Clerk proxy URL - transforms empty strings to undefined
  VITE_CLERK_PROXY_URL: z
    .string()
    .transform((value) => (value ? value : undefined))
    .optional(),

  // Sentry DSN for error tracking
  VITE_DSN: requiredHttpUrl("VITE_DSN is required", "Invalid DSN"),

  // Smartlook analytics key
  VITE_SMARTLOOK_KEY: z.string().min(1, "VITE_SMARTLOOK_KEY is required"),

  // Mixpanel analytics token
  VITE_MIXPANEL_TOKEN: z.string().min(1, "VITE_MIXPANEL_TOKEN is required"),

  // RevenueCat public keys for in-app purchases (optional)
  VITE_IOS_REVENUE_CAT_PUBLIC_KEY: z.string().optional(),
  VITE_ANDROID_REVENUE_CAT_PUBLIC_KEY: z.string().optional(),
});

export type Env = z.infer<typeof envSchema>;

export const parseEnv = (rawEnv: unknown): Env => envSchema.parse(rawEnv);

// Validate and export environment variables
export const env = parseEnv(import.meta.env);
